import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './SettingsForm.css';
import { FaArrowLeft, FaRocket, FaUnlockAlt, FaLanguage, FaCog, FaMapMarkerAlt, FaEnvelope, FaBell, FaBellSlash, FaCalendarAlt } from 'react-icons/fa';
import { CustomButton8, CustomButton2, CustomButton10Ca, InputPassword1, CustomInputBox1, ModernSwitch } from "./Templates.js";
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider';
import { useTranslation } from 'react-i18next';
import Spinner from './common/Spinner';
import { useToast } from './hooks/ToastContext';
import SimpleAccountDelete from './SimpleAccountDelete';
import LocationAlertsSection from './LocationAlerts/LocationAlertsSection';

const SettingsForm = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const { user, updateLanguagePreference } = useContext(AuthContext);
  const { addToast } = useToast();
  const locationInputRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  
  // Form states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'en');
  const [locationData, setLocationData] = useState({
    city: '',
    city_lat: null,
    city_lng: null
  });
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [locationAlertsEnabled, setLocationAlertsEnabled] = useState(false);
  const [alertFrequency, setAlertFrequency] = useState('daily');
  const [savingAlertSettings, setSavingAlertSettings] = useState(false);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [languageLoading, setLanguageLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [emailNotificationsLoading, setEmailNotificationsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Set the initial language selection based on i18n
    setSelectedLanguage(i18n.language || 'en');
    
    // Load current user location and email preferences from profile
    if (user?.profile) {
      setLocationData({
        city: user.profile.city || '',
        city_lat: user.profile.city_lat,
        city_lng: user.profile.city_lng
      });
      // Default to true if not set (new feature)
      setEmailNotificationsEnabled(user.profile.email_notifications_enabled !== false);
      
      // Load location alert preferences
      const frequency = user.profile.location_alert_frequency || 'daily';
      setAlertFrequency(frequency);
      setLocationAlertsEnabled(frequency !== 'never');
    }
    
    // Check if Google Maps is loaded
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
        // Initialize autocomplete after maps is loaded
        setTimeout(() => {
          initializeLocationAutocomplete();
        }, 100);
      } else {
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };
    
    checkGoogleMapsLoaded();
  }, [i18n.language, user]);

  const handleBackClick = () => {
    navigate(-1);
  };
  
  // Initialize Google Places Autocomplete for location
  const initializeLocationAutocomplete = useCallback(() => {
    if (!locationInputRef.current || !googleMapsLoaded) return;
    
    try {
      const autocomplete = new window.google.maps.places.Autocomplete(
        locationInputRef.current.querySelector('input'),
        {
          types: ['(cities)'],
          // Remove country restriction to allow international cities
        }
      );
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          setLocationData({
            city: place.formatted_address,
            city_lat: place.geometry.location.lat(),
            city_lng: place.geometry.location.lng()
          });
        }
      });
    } catch (error) {
      console.error('Error initializing location autocomplete:', error);
    }
  }, [googleMapsLoaded]);
  
  // Get user's current location
  const detectCurrentLocation = async () => {
    setLocationLoading(true);
    
    try {
      if (!navigator.geolocation) {
        throw new Error(t('profile_setup.geolocation_not_supported'));
      }
      
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000
          }
        );
      });
      
      const { latitude, longitude } = position.coords;
      
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        const latLng = new window.google.maps.LatLng(latitude, longitude);
        
        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const cityComponent = results[0].address_components.find(
              component => component.types.includes('locality') || 
                          component.types.includes('administrative_area_level_1')
            );
            
            const countryComponent = results[0].address_components.find(
              component => component.types.includes('country')
            );
            
            let locationName = results[0].formatted_address;
            
            if (cityComponent && countryComponent) {
              locationName = `${cityComponent.long_name}, ${countryComponent.long_name}`;
            } else if (countryComponent) {
              const adminArea = results[0].address_components.find(
                component => component.types.includes('administrative_area_level_1')
              );
              if (adminArea) {
                locationName = `${adminArea.long_name}, ${countryComponent.long_name}`;
              } else {
                locationName = countryComponent.long_name;
              }
            }
            
            setLocationData({
              city: locationName,
              city_lat: latitude,
              city_lng: longitude
            });
            
            addToast({
              type: 'success',
              title: t('settings.success'),
              description: t('profile_setup.location_detection_info')
            });
          } else {
            throw new Error(t('profile_setup.geocoding_failed'));
          }
        });
      } else {
        // Fallback: just store coordinates
        setLocationData(prev => ({
          ...prev,
          city_lat: latitude,
          city_lng: longitude
        }));
      }
    } catch (error) {
      console.error('Location detection error:', error);
      let errorMessage = t('profile_setup.location_detection_failed');
      
      if (error.code === 1) {
        errorMessage = t('profile_setup.location_permission_denied');
      } else if (error.code === 2) {
        errorMessage = t('profile_setup.location_unavailable');
      } else if (error.code === 3) {
        errorMessage = t('profile_setup.location_timeout');
      }
      
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: errorMessage
      });
    } finally {
      setLocationLoading(false);
    }
  };
  
  // Save location to profile
  const handleLocationSave = async () => {
    if (!user?.id || !locationData.city) {
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('errors.city_required')
      });
      return;
    }
    
    try {
      setLocationLoading(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          city: locationData.city,
          city_lat: locationData.city_lat,
          city_lng: locationData.city_lng,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      addToast({
      type: 'success',
      title: t('settings.success'),
      description: t('settings.location_updated')
      });
      
      // Refresh the page to update the context
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Error updating location:', error);
      addToast({
      type: 'error',
      title: t('settings.error'),
      description: t('settings.location_update_failed')
      });
    } finally {
      setLocationLoading(false);
    }
  };

  const validatePasswordForm = () => {
    // Reset error and success states
    setError(null);
    setSuccessMessage('');

    // Validate inputs
    if (!currentPassword) {
      setError(t('settings.current_password_required'));
      return false;
    }

    if (!newPassword) {
      setError(t('settings.new_password_required'));
      return false;
    }

    if (newPassword.length < 6) {
      setError(t('settings.password_length'));
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError(t('settings.passwords_dont_match'));
      return false;
    }

    return true;
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }

    try {
      setLoading(true);

      // First verify the current password is correct
      const { data: { user }, error: sessionError } = await supabase.auth.getUser();
      
      if (sessionError) {
        throw new Error(t('settings.auth_error'));
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        throw new Error(updateError.message);
      }

      // Success
      setSuccessMessage(t('settings.password_updated'));
      addToast({
        type: 'success',
        title: t('settings.success'),
        description: t('settings.password_updated')
      });

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

    } catch (error) {
      console.error('Error changing password:', error);
      setError(error.message || t('settings.unknown_error'));
      
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: error.message || t('settings.password_change_failed')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (e) => {
    const newLanguage = e.target.value;
    
    try {
      setLanguageLoading(true);
      setSelectedLanguage(newLanguage);
      
      // First update i18n
      await i18n.changeLanguage(newLanguage);
      
      // Then update user preference if logged in
      if (updateLanguagePreference) {
        const success = await updateLanguagePreference(newLanguage);
        
        if (success) {
          addToast({
            type: 'success',
            title: t('settings.success'),
            description: t('settings.language_updated')
          });
        } else {
          console.warn('Language preference not saved to profile');
        }
      }
    } catch (error) {
      console.error('Error changing language:', error);
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.language_update_failed')
      });
    } finally {
      setLanguageLoading(false);
    }
  };

  // Handle email notifications toggle
  const handleEmailNotificationsToggle = async (enabled) => {
    if (!user?.id) {
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('errors.error_fetch_user')
      });
      return;
    }
    
    try {
      setEmailNotificationsLoading(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          email_notifications_enabled: enabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      setEmailNotificationsEnabled(enabled);
      
      addToast({
        type: 'success',
        title: t('settings.success'),
        description: enabled 
          ? t('settings.email_notifications_enabled')
          : t('settings.email_notifications_disabled')
      });
      
    } catch (error) {
      console.error('Error updating email notifications:', error);
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.email_notifications_update_failed')
      });
      // Revert toggle state on error
      setEmailNotificationsEnabled(!enabled);
    } finally {
      setEmailNotificationsLoading(false);
    }
  };

  // Handle location alerts enable/disable
  const handleLocationAlertsToggle = async (enabled) => {
    if (!user?.id) return;
    
    try {
      setSavingAlertSettings(true);
      
      const newFrequency = enabled ? alertFrequency : 'never';
      
      const { error } = await supabase
        .from('profiles')
        .update({
          location_alert_frequency: newFrequency,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      setLocationAlertsEnabled(enabled);
      
      addToast({
        type: 'success',
        title: t('settings.success'),
        description: enabled 
          ? (t('settings.location_alerts_enabled') || 'Location alerts enabled')
          : (t('settings.location_alerts_disabled') || 'Location alerts disabled')
      });
      
    } catch (error) {
      console.error('Error updating location alerts:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('settings.location_alerts_update_failed') || 'Failed to update location alerts'
      });
      setLocationAlertsEnabled(!enabled);
    } finally {
      setSavingAlertSettings(false);
    }
  };

  // Handle alert frequency change (instant/daily)
  const handleAlertFrequencyChange = async (newFrequency) => {
    if (!user?.id) return;
    
    try {
      setSavingAlertSettings(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          location_alert_frequency: newFrequency,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      setAlertFrequency(newFrequency);
      
      addToast({
        type: 'success',
        title: t('settings.success'),
        description: newFrequency === 'instant' 
          ? (t('settings.switched_to_instant') || 'Switched to instant alerts')
          : (t('settings.switched_to_daily') || 'Switched to daily digest')
      });
      
    } catch (error) {
      console.error('Error updating alert frequency:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('settings.alert_frequency_update_failed') || 'Failed to update alert frequency'
      });
    } finally {
      setSavingAlertSettings(false);
    }
  };

  return (
    <div className="settings-container">
        <div className="back-button">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft/>
          </CustomButton8>
        </div>

        {/* Location Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <FaMapMarkerAlt />
            <h3>{t('settings.location_settings')}</h3>
          </div>

          <div className="form-group" ref={locationInputRef}>
            <label htmlFor="location">{t('settings.your_location')}:</label>
            <CustomInputBox1
              value={locationData.city}
              onChange={(e) => setLocationData(prev => ({ ...prev, city: e.target.value }))}
              placeholder="Enter your city or location"
              disabled={locationLoading || !googleMapsLoaded}
            />
            <div className="location-actions">
              <button
                type="button"
                className="detect-location-btn"
                onClick={detectCurrentLocation}
                disabled={locationLoading}
                title={t('settings.detect_location')}
              >
                {locationLoading ? (
                  <Spinner size="small" showText={false} thickness={2} />
                ) : (
                  <>📍 {t('settings.detect_location')}</>
                )}
              </button>
              <CustomButton2
                onClick={handleLocationSave}
                label={locationLoading ? t('settings.saving') : t('settings.save_location')}
                disabled={locationLoading || !locationData.city}
              >
                {locationLoading && <Spinner size="small" showText={false} thickness={2} color="var(--BackG-color)" />}
              </CustomButton2>
            </div>
            <p className="help-text">{t('settings.location_help_text')}</p>
          </div>

          {/* Favorite Locations for Alerts */}
          <LocationAlertsSection />
        </div>

        {/* Notification Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <FaEnvelope />
            <h3>{t('settings.notification_settings')}</h3>
          </div>
          
          <p className="section-description">
            {t('settings.email_notifications_description')}
          </p>
          
          <ModernSwitch
            label={emailNotificationsEnabled ? t('settings.enabled') : t('settings.disabled')}
            checked={emailNotificationsEnabled}
            onChange={(e) => handleEmailNotificationsToggle(e.target.checked)}
            disabled={emailNotificationsLoading}
            leftIcon={<FaBellSlash />}
            rightIcon={<FaBell />}
            className={emailNotificationsLoading ? 'loading' : ''}
          />

          {/* Location Alerts Settings */}
          <div className="location-alerts-settings">
            <div className="alert-settings-header">
              <h4>📍 {t('settings.location_alerts') || 'Location Alerts'}</h4>
              <p className="help-text">{t('settings.location_alerts_help') || 'Get notified about rides/packages near your favorite locations'}</p>
            </div>
            
            <ModernSwitch
              label={locationAlertsEnabled ? t('settings.enabled') : t('settings.disabled')}
              checked={locationAlertsEnabled}
              onChange={(e) => handleLocationAlertsToggle(e.target.checked)}
              disabled={savingAlertSettings}
              leftIcon={<FaBellSlash />}
              rightIcon={<FaBell />}
              className={savingAlertSettings ? 'loading' : ''}
            />

            {locationAlertsEnabled && (
              <div className="alert-frequency-control">
                <p className="frequency-help-text">
                  {alertFrequency === 'instant' 
                    ? '⚡ ' + (t('settings.instant_alerts_help') || 'You will receive emails immediately when matches are found')
                    : '📅 ' + (t('settings.daily_alerts_help') || 'You will receive one summary email per day with all matches')}
                </p>
                <ModernSwitch
                  label={alertFrequency === 'instant' ? t('settings.instant') : t('settings.daily')}
                  checked={alertFrequency === 'instant'}
                  onChange={(e) => handleAlertFrequencyChange(e.target.checked ? 'instant' : 'daily')}
                  disabled={savingAlertSettings}
                  leftIcon={<FaCalendarAlt />}
                  rightIcon={<FaRocket />}
                  className={savingAlertSettings ? 'loading' : ''}
                />
              </div>
            )}
          </div>
        </div>
        
        <h2>{t('navigation.settings')}</h2>

        <div className="settings-section">
          <div className="section-header">
            <FaUnlockAlt />
            <h3>{t('settings.change_password')}</h3>
          </div>

          <form onSubmit={handlePasswordChange}>
            <div className="form-group">
              <label className="password-label">{t('settings.current_password')}</label>
              <InputPassword1
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="password-label">{t('settings.new_password')}</label>
              <InputPassword1
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="password-label">{t('settings.confirm_password')}</label>
              <InputPassword1
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <div className="form-actions">
              <CustomButton2
                type="submit"
                label={loading ? t('settings.saving') : t('settings.update_password')}
                disabled={loading}
              >
                {loading && <Spinner size="small" showText={false} thickness={2} color="var(--BackG-color)" />}
              </CustomButton2>
            </div>
          </form>
        </div>

        <div className="settings-section">
          <div className="section-header">
            <FaLanguage />
            <h3>{t('settings.language_settings')}</h3>
          </div>

          <div className="form-group">
            <label htmlFor="language">{t('settings.language')}:</label>
            <div className="select-container">
              <select
                id="language"
                value={selectedLanguage}
                onChange={handleLanguageChange}
                disabled={languageLoading}
                className="language-select"
              >
                <option value="en">{t('languages.english')}</option>
                <option value="lv">{t('languages.latvian')}</option>
                <option value="ru">{t('languages.russian')}</option>
              </select>
              {languageLoading && (
                <div className="select-loading">
                  <Spinner size="small" showText={false} thickness={2} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DANGER ZONE */}
        <div className="settings-section danger-zone">
          <div className="section-header">
            <FaCog />
            <h3>⚠️ {t('settings.danger_zone')}</h3>
          </div>
          <p className="danger-zone-description">
            {t('settings.danger_zone_description')}
          </p>
          
          <div className="danger-item">
            <div className="danger-item-info">
              <h4>{t('settings.delete_account')}</h4>
              <p>{t('settings.delete_account_soft_description')}</p>
            </div>
            <SimpleAccountDelete />
          </div>
          
          <div className="danger-item data-deletion-info">
            <div className="danger-item-info">
              <h4>{t('settings.complete_data_deletion')}</h4>
              <p>{t('settings.complete_data_deletion_description')}</p>
              <p className="contact-support">
                {t('settings.contact_support_text')}{' '}
                <a href="mailto:support@brauksimkopa.lv" className="support-email">
                  support@brauksimkopa.lv
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
  );
};

export default SettingsForm;
